<template>
  <!-- ======= Company Numbers Section ======= -->
  <section id="company-numbers" class="company-numbers">
    <div class="container" data-aos="fade-up">
      <div class="row justify-content-center">
        <div class="col-lg-12 text-center mb-5">
          <h3 class="section-title">{{ $t("CompanyNumbers") }}</h3>
        </div>
      </div>

      <div class="row">
        <div
          v-for="item in numbers"
          :key="item.id"
          class="col-lg-3 col-md-6 text-center mb-4"
        >
          <div class="number-card">
            <div class="icon-wrapper">
              <img
                :src="$store.state.server + item.img"
                alt="company number"
                class="img-fluid"
              />
            </div>
            <h4 class="number-title">
              {{ $i18n.locale === "ar" ? item.name_ar : item.name_en }}
            </h4>
            <div class="d-flex">
              <h2 class="counter" :data-id="item.id">
                {{ counters[item.id] }}
              </h2>
              <p class="number-description mx-3">
                {{
                  $i18n.locale === "ar"
                    ? item.description_ar
                    : item.description_en
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <!-- End Company Numbers Section -->
</template>

<script>
export default {
  name: "CompanyNumbers",
  props: {
    numbers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      counters: {}, // holds animated values
      observer: null,
    };
  },
  mounted() {
    // Initialize counters at 0
    this.numbers.forEach((item) => {
      this.counters[item.id] = 0;
    });

    // Intersection Observer to trigger animation
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-id");
            const target = this.numbers.find((n) => n.id == id)?.number || 0;
            this.animateCounter(id, target);
            this.observer.unobserve(entry.target); // Run once per item
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe each counter
    this.$nextTick(() => {
      document.querySelectorAll(".counter").forEach((el) => {
        this.observer.observe(el);
      });
    });
  },
  methods: {
    animateCounter(id, target) {
      let start = 0;
      const step = () => {
        start += Math.ceil(target / 50); // speed control
        if (start >= target) {
          this.counters[id] = target;
        } else {
          this.counters[id] = start;
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    },
  },
};
</script>

<style scoped>
.company-numbers {
  padding: 80px 0;
  background: var(--color-background);
  position: relative;
}

.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-primary);
  position: relative;
  display: inline-block;
  padding-bottom: 10px;
}

.section-title::after {
  content: "";
  width: 60px;
  height: 3px;
  background: var(--color-secondary);
  display: block;
  margin: 10px auto 0;
  border-radius: 2px;
}

.number-card {
  background: #fff;
  padding: 30px 20px;
  border-radius: 20px;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.number-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.icon-wrapper {
  width: 90px;
  height: 90px;
  margin: 0 auto 20px;
  background: linear-gradient(
    135deg,
    var(--color-primary),
    var(--color-secondary)
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.icon-wrapper img {
  max-width: 50%;
  max-height: 50%;
}

.number-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--color-default);
  margin-bottom: 10px;
}

.number-description {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 15px;
  min-height: 50px;
}

.counter {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--color-secondary);
}
</style>
